import type { BaseSyntheticEvent } from 'react'
import { useForm, type UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'

import type { AffiliateSocialLinkSchema } from '@/api/gen'
import {
  buildSocialLinks,
  socialLinksFormSchema,
  type SocialLinksFormValues,
  socialLinksToForm,
} from '@/domain/social-links'

export type SocialLinksForm = UseFormReturn<SocialLinksFormValues>

type SocialLinkFieldError = { index: number; field: 'platform' | 'label' | 'url'; message: string }

const ROW_FIELDS = new Set(['platform', 'label', 'url'])

/** The mapped per-row errors, plus whether the 422 also carried an entry that
 *  did NOT map to a `social_links` row field (a non-link error, or a link error
 *  whose `loc` shape we don't recognise). `hadUnmapped` gates whether the caller
 *  still needs to surface its generic alert + Sentry for the unmapped remainder. */
type ExtractedSocialLinkErrors = { rowErrors: SocialLinkFieldError[]; hadUnmapped: boolean }

// Parse ONE 422 error entry into a row-field error, or `null` when it isn't a
// `social_links.<index>.<field>` triple we can hang on a specific row — i.e. a
// non-link error, or a `social_links` error whose loc shape didn't match.
const parseRowError = (entry: unknown): SocialLinkFieldError | null => {
  if (typeof entry !== 'object' || entry === null) {
    return null
  }
  const loc = (entry as { loc?: unknown }).loc
  const msg = (entry as { msg?: unknown }).msg
  if (!Array.isArray(loc) || typeof msg !== 'string' || msg === '') {
    return null
  }
  const anchor = loc.indexOf('social_links')
  const index: unknown = loc[anchor + 1]
  const field: unknown = loc[anchor + 2]
  if (
    anchor !== -1 &&
    typeof index === 'number' &&
    typeof field === 'string' &&
    ROW_FIELDS.has(field)
  ) {
    return { index, field: field as SocialLinkFieldError['field'], message: msg }
  }

  return null
}

/**
 * Pull per-row `social_links` errors out of a Django Ninja schema 422, preserving
 * the array INDEX so each maps onto its `links.<i>.<field>` form path. The shared
 * `extractValidationFieldErrors` collapses `loc` to its last segment (dropping the
 * index), which can't address a specific row — so this reads the raw `loc` and
 * finds the `social_links` → index → field triple. Returns `null` for any shape
 * that isn't a parseable 422 envelope (a non-422, a service-level
 * `CustomValidationError`, or non-array `errors`), so the caller falls back to the
 * inline alert. Otherwise returns every mapped `rowErrors` AND `hadUnmapped` — set
 * when the SAME 422 also carried a non-`social_links` error (e.g. a legacy scalar
 * field with no client validation) or a link error whose loc didn't match — so the
 * caller can still fire its alert + Sentry for what this couldn't surface per-row.
 * Sentry observability is untouched — this only feeds per-field surfacing.
 */
const extractSocialLinkFieldErrors = (error: unknown): ExtractedSocialLinkErrors | null => {
  if (!isAxiosError(error) || error.response?.status !== 422) {
    return null
  }
  const data: unknown = error.response.data
  if (typeof data !== 'object' || data === null) {
    return null
  }
  const errors = (data as { data?: { errors?: unknown } }).data?.errors
  if (!Array.isArray(errors)) {
    return null
  }
  const rowErrors: SocialLinkFieldError[] = []
  let hadUnmapped = false
  for (const entry of errors) {
    const rowError = parseRowError(entry)
    if (rowError === null) {
      hadUnmapped = true
    } else {
      rowErrors.push(rowError)
    }
  }

  return { rowErrors, hadUnmapped }
}

export type UseSocialLinksForm = {
  form: SocialLinksForm
  /** Prefill the editor from the saved wire list; `undefined`/`[]` opens it empty. */
  reset: (links?: AffiliateSocialLinkSchema[]) => void
  /** Submit handler for the `<form>`: validates via the resolver, and on a valid
   *  form assembles the full-list write body (via `buildSocialLinks`, the same
   *  builder the schema validates through) and hands it to `onValid`. */
  handleSubmit: (
    onValid: (links: AffiliateSocialLinkSchema[]) => void,
  ) => (event?: BaseSyntheticEvent) => void
  /** Hang a server 422's per-row `social_links` errors on the form via `setError`.
   *  Returns `true` only when at least one error mapped to a row AND none was left
   *  unmapped, so the caller skips its generic alert; if any error is unmapped (a
   *  non-link error, or a loc that doesn't name `social_links`) it returns `false`
   *  so the caller's alert + Sentry still fire. */
  applyServerErrors: (error: unknown) => boolean
}

export const useSocialLinksForm = (): UseSocialLinksForm => {
  const form = useForm<SocialLinksFormValues>({
    resolver: zodResolver(socialLinksFormSchema),
    defaultValues: { links: [] },
  })

  const reset = (links?: AffiliateSocialLinkSchema[]) => {
    form.reset(socialLinksToForm(links))
  }

  const handleSubmit = (onValid: (links: AffiliateSocialLinkSchema[]) => void) =>
    form.handleSubmit((values) => {
      onValid(buildSocialLinks(values))
    })

  const applyServerErrors = (error: unknown): boolean => {
    const extracted = extractSocialLinkFieldErrors(error)
    if (extracted === null) {
      return false
    }
    const { rowErrors, hadUnmapped } = extracted
    for (const { index, field, message } of rowErrors) {
      form.setError(`links.${index}.${field}`, { type: 'server', message })
    }

    // Claim "fully handled" (caller skips its generic alert) ONLY when EVERY entry
    // in the 422 mapped to a row field. The mapped row errors are surfaced above
    // regardless; but if any entry was unmapped — a non-link error, or a link
    // error whose loc shape didn't match — return `false` so the caller's inline
    // alert + Sentry still fire for that unmapped remainder (no silent failure).
    return rowErrors.length > 0 && !hadUnmapped
  }

  return { form, reset, handleSubmit, applyServerErrors }
}
